<script setup lang="ts">
// Inlined original SigninAdmin.vue
import { onMounted, ref } from 'vue'
import { signInAdmin } from '@/services/authSignin'
import { useDisplay } from 'vuetify'
import router from '@/router'
import Cookies from 'universal-cookie'
import { fetchAllUsers } from '@/api/accountsClient'
import ProgressLoader from '@/components/ProgressLoader.vue'
import * as UAParser from 'ua-parser-js'
import { sendLogInfo } from '@/api/accountsAdmin'
import NewPasswordDialog from '@/components/NewPasswordDialog.vue'
import { useNewPasswordDialog } from '@/composables/useNewPasswordDialog'

export interface UserContext {
  loginMethod: string
  userAgent: string
  ipAddress: string
  deviceInfo: string
}

const loginInfo = ref<UserContext>({
  loginMethod: '',
  userAgent: '',
  ipAddress: '',
  deviceInfo: '',
})
async function buildUserContext(loginMethod = 'Cognito') {
  const parser = new UAParser.UAParser(window.navigator.userAgent)
  const ua = parser.getResult()
  const deviceInfo = `${ua.browser.name} ${ua.browser.version} on ${ua.os.name} ${ua.os.version}`
  let ipAddress = ''
  try {
    const res = await fetch('https://api.ipify.org?format=json')
    const data = await res.json()
    ipAddress = data.ip
  } catch (err) {
    console.warn('Could not fetch IP:', err)
  }
  loginInfo.value = { loginMethod, userAgent: window.navigator.userAgent, ipAddress, deviceInfo }
}

const cookei = new Cookies()
const { xs } = useDisplay()
const username = ref('')
const juid1 = ref('')
const password = ref('')
const message = ref('')
const snackbar = ref(false)
import { useUserStore } from '@/stores/userStore'
import axios from 'axios'
import { axiosInstance } from '@/api'
const isLoading = ref(false)
const showPassword = ref(false)
const tokenLoader = ref(false)
const userStore = useUserStore()
const { isDialogOpen, submitNewPassword, cancelDialog } = useNewPasswordDialog()
const passwordDialogRef = ref()
async function handlePasswordSubmit(newPassword: string) {
  try {
    passwordDialogRef.value?.setLoading(true)
    await submitNewPassword(newPassword)
  } catch (error: any) {
    console.error('Error setting password:', error)
    passwordDialogRef.value?.setError(error.message || 'Failed to set password. Please try again.')
  }
}

const handleSignIn = async () => {
  isLoading.value = true
  try {
    isLoading.value = true
    const response = await signInAdmin(username.value, password.value)
    if (response) {
      sendLogInfo(loginInfo.value)
    }
    const role = response?.accessToken?.payload?.['cognito:groups'][0]
    const idToken = response?.idToken?.jwtToken
    const urlParams = new URLSearchParams(window.location.search)
    const redirect = urlParams.get('redirect')
    const redirectdomain = urlParams.get('redirectdomain')
    if (redirect) {
      const hasQueryParams = redirectdomain.includes('?')
      if (hasQueryParams === false) {
        window.location.href = `${redirectdomain}?token=${idToken}&role=${role}&redirectionDo=true`
      } else {
        window.location.href = `${redirectdomain}&token=${idToken}&role=${role}&redirectionDo=true`
      }
    } else {
      if (
        role == 'company_user' ||
        role == 'company_admin' ||
        role == 'company_suadmin' ||
        role == 'company_owner'
      ) {
        router.push('/account-admin')
      }
    }
    message.value = '✅ Sign-in successful!'
    snackbar.value = true
    isLoading.value = false
  } catch (error: any) {
    console.log(error)
    message.value = error.message || '❌ Sign-in failed.'
    snackbar.value = true
    isLoading.value = false
  } finally {
    isLoading.value = false
  }
}

onMounted(async () => {
  buildUserContext()
  const urlParams = new URLSearchParams(window.location.search)
  const redirect = urlParams.get('redirect')
  const redirectdomain = urlParams.get('redirectdomain')
  if (redirect) {
    try {
      tokenLoader.value = true
      const hasQueryParams = redirectdomain.includes('?')
      const data = await fetchAllUsers()
      if (hasQueryParams === false && data) {
        window.location.href = `${redirectdomain}?token=${userStore.token}&role=${userStore.role}&redirectionDo=true${userStore?.juid ? `&juid=${userStore.juid}` : ''}`
      } else if (hasQueryParams === true && data) {
        window.location.href = `${redirectdomain}&token=${userStore.token}&role=${userStore.role}&redirectionDo=true${userStore?.juid ? `&juid=${userStore.juid}` : ''}`
      }
      tokenLoader.value = false
    } catch (error) {
      tokenLoader.value = false
    }
  } else if (!redirect) {
    if (userStore?.role && userStore?.token) {
      if (
        userStore?.role == 'company_user' ||
        userStore?.role == 'company_admin' ||
        userStore?.role == 'company_suadmin' ||
        userStore?.role == 'company_owner'
      ) {
        router.push('/account-admin')
      }
    }
  }
})
const required = (v: string) => !!v || 'This field is required'
</script>

<template>
  <v-container fluid class="page-wrapper">
    <template v-if="tokenLoader">
      <div class="loader-wrapper">
        <progress-loader />
      </div>
    </template>
    <template v-else>
      <div class="auth-container">
        <h2 class="brand">SodaSoft.io</h2>
        <v-card class="auth-card" :max-width="xs ? '100%' : 440">
          <div class="text-center mb-8">
            <h1 class="welcome">Welcome Back</h1>
            <p class="subtitle">Log in to manage your products, Accounts & Users</p>
          </div>
          <v-form @submit.prevent="handleSignIn">
            <div class="input-group">
              <input
                type="text"
                placeholder="Username"
                v-model="username"
                class="input"
                :rules="[required]"
              />
              <div class="password-wrapper">
                <input
                  :type="showPassword ? 'text' : 'password'"
                  v-model="password"
                  placeholder="Password"
                  class="input"
                />
                <button type="button" class="eye-btn" @click="showPassword = !showPassword">
                  <i :class="showPassword ? 'mdi mdi-eye-off' : 'mdi mdi-eye'"></i>
                </button>
              </div>
            </div>
            <v-btn
              block
              color="#3D7FD8"
              :disabled="isLoading"
              type="submit"
              height="48"
              class="login-btn mt-5"
            >
              Log In
            </v-btn>
            <div class="text-center mt-4">
              <span class="forgot-link" @click="router.push('/reset-password-admin')">
                Forgot Password?
              </span>
            </div>
            <p class="signup-text">
              Don't have an account? use your company Email to
              <span class="signup-link" @click="router.push('/company/signup-admin')">Sign Up</span>
            </p>
            <div class="separator"><span>OR</span></div>
            <p class="client-question">Are you a SodaSoft Client?</p>
            <div class="client-portal-link" @click="router.push('/')">
              Log in to Client Portal
              <v-icon small class="ml-1">mdi-arrow-right</v-icon>
            </div>
          </v-form>
        </v-card>
        <v-snackbar v-model="snackbar" color="info" :timeout="3000">
          {{ message }}
          <template #action="{ attrs }">
            <v-btn text v-bind="attrs" @click="snackbar = false">Close</v-btn>
          </template>
        </v-snackbar>
        <NewPasswordDialog
          ref="passwordDialogRef"
          v-model="isDialogOpen"
          @confirm="handlePasswordSubmit"
          @cancel="cancelDialog"
        />
      </div>
    </template>
  </v-container>
</template>

<style scoped>
/* Page wrapper */
.page-wrapper {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #cacaca;
  padding: 24px 16px;
}

.loader-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
}

.auth-container {
  width: 100%;
  max-width: 440px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* Brand */
.brand {
  font-size: 24px;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 28px;
  text-align: left;
  align-self: flex-start;
}

/* Card */
.auth-card {
  width: 100%;
  border-radius: 16px;
  padding: 44px 36px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* Headings */
.welcome {
  font-size: 36px;
  font-weight: 600;
  color: #1a1a1a;
  line-height: 1.2;
}

.subtitle {
  font-size: 14px;
  color: #5a5a5a;
  margin-top: 8px;
  line-height: 1.5;
}

/* Input group */
.input-group {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 4px;
}

.input {
  width: 100%;
  height: 48px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 0 16px;
  font-size: 14px;
  color: #1a1a1a;
  background: white;
  transition: border-color 0.2s;
}

.input:focus {
  outline: none;
  border-color: #4a90e2;
}

.input::placeholder {
  color: #aaaaaa;
}

.password-wrapper {
  position: relative;
  width: 100%;
}

.password-wrapper .input {
  padding-right: 48px;
}

.eye-btn {
  position: absolute;
  top: 50%;
  right: 14px;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  cursor: pointer;
  color: #666;
  padding: 4px;
}

.eye-btn:hover {
  color: #333;
}

.eye-btn .mdi {
  font-size: 20px;
}

/* Button */
.login-btn {
  border-radius: 8px;
  text-transform: none;
  font-size: 15px;
  font-weight: 500;
  letter-spacing: 0;
  box-shadow: none !important;
}

/* Links */
.forgot-link {
  color: #5b7c99;
  font-size: 13px;
  cursor: pointer;
  font-weight: 500;
}

.forgot-link:hover {
  text-decoration: underline;
}

.signup-text {
  font-size: 13px;
  color: #5a5a5a;
  text-align: center;
  margin-top: 12px;
  margin-bottom: 0;
}

.signup-link {
  color: #4a90e2;
  font-weight: 600;
  cursor: pointer;
}

.signup-link:hover {
  text-decoration: underline;
}

/* Separator */
.separator {
  display: flex;
  align-items: center;
  margin: 22px 0;
  gap: 16px;
}

.separator::before,
.separator::after {
  content: '';
  flex: 1;
  height: 1px;
  background: #e0e0e0;
}

.separator span {
  font-size: 13px;
  color: #999;
  font-weight: 500;
}

/* Client portal section */
.client-question {
  font-size: 15px;
  font-weight: 500;
  color: #1a1a1a;
  text-align: center;
  margin-bottom: 10px;
}

.client-portal-link {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #5b7c99;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  padding: 8px;
  border-radius: 6px;
  transition: background-color 0.2s;
}

.client-portal-link:hover {
  background-color: #f5f5f5;
}

/* Autofill fix */
input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus {
  -webkit-box-shadow: 0 0 0px 1000px white inset;
  -webkit-text-fill-color: #1a1a1a;
  transition: background-color 5000s ease-in-out 0s;
}
</style>
